import { useState, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Card, Badge, Input, cn } from '@pantry/ui';
import {
  useAuditLogs,
  type AuditLogEntry,
  type AuditLogQueryParams,
  AUDIT_ACTIONS,
  AUDIT_TARGET_TYPES,
  getAuditActionDescription,
} from '@pantry/api-client';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AuditLogDetailSheet } from './audit-log-detail-sheet';

// Target type colors
const TARGET_TYPE_COLORS: Record<string, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  announcement: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  product: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  contribution: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  subscription: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  household: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  job: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  translation: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  system: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

// Action category labels
function getActionCategories(t: ReturnType<typeof useLingui>['t']): Record<string, string> {
  return {
    user: t`User Management`,
    announcement: t`Announcements`,
    product: t`Products`,
    moderation: t`Moderation`,
    subscription: t`Subscriptions`,
    bulk: t`Bulk Operations`,
    system: t`System`,
    support: t`Support`,
    translation: t`Translations`,
  };
}

interface AuditLogRowProps {
  log: AuditLogEntry;
  onSelect: (log: AuditLogEntry) => void;
}

function AuditLogRow({ log, onSelect }: AuditLogRowProps) {
  const { t, i18n } = useLingui();
  const actionDescription = AUDIT_ACTIONS[log.action as keyof typeof AUDIT_ACTIONS] || log.action;

  return (
    <tr
      className="group border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect(log)}
    >
      {/* Timestamp */}
      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
        {formatDateTime(log.createdAt, i18n.locale)}
      </td>

      {/* Admin */}
      <td className="px-4 py-3">
        <div className="min-w-0">
          <span className="font-medium text-foreground block truncate max-w-[180px]">
            {log.admin.name || log.admin.email}
          </span>
          {log.admin.name && (
            <span className="text-sm text-muted-foreground truncate block max-w-[180px]">
              {log.admin.email}
            </span>
          )}
        </div>
      </td>

      {/* Action */}
      <td className="px-4 py-3">
        <span className="font-medium text-foreground">{actionDescription}</span>
      </td>

      {/* Target */}
      <td className="px-4 py-3">
        <Badge
          variant="secondary"
          className={cn('font-medium capitalize', TARGET_TYPE_COLORS[log.targetType] || '')}
        >
          {log.targetType}
        </Badge>
      </td>

      {/* Details Preview */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        <span className="truncate block max-w-[200px]">{getDetailsPreview(log.details, t)}</span>
      </td>
    </tr>
  );
}

function formatDateTime(date: string, locale: string): string {
  const d = new Date(date);
  return d.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getDetailsPreview(
  details: Record<string, unknown>,
  t: ReturnType<typeof useLingui>['t']
): string {
  // Extract key details for preview
  const { adminEmail, ...rest } = details;
  const keys = Object.keys(rest);
  if (keys.length === 0) return '-';

  // Try to show meaningful preview
  if (rest.targetEmail) return String(rest.targetEmail);
  if (rest.title) return String(rest.title);
  if (rest.name) return String(rest.name);
  if (rest.previousRole && rest.newRole) return `${rest.previousRole} → ${rest.newRole}`;

  // Fallback to first key/value
  const firstKey = keys[0];
  const value = rest[firstKey];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return t`${keys.length} fields`;
}

type TargetTypeFilter = (typeof AUDIT_TARGET_TYPES)[number] | 'all';
type ActionFilter = keyof typeof AUDIT_ACTIONS | 'all';

function getTargetTypeLabel(type: string, t: ReturnType<typeof useLingui>['t']): string {
  switch (type) {
    case 'user':
      return t`User`;
    case 'announcement':
      return t`Announcement`;
    case 'product':
      return t`Product`;
    case 'contribution':
      return t`Contribution`;
    case 'subscription':
      return t`Subscription`;
    case 'household':
      return t`Household`;
    case 'job':
      return t`Job`;
    case 'translation':
      return t`Translation`;
    case 'system':
      return t`System`;
    default:
      return type;
  }
}

function getTargetTypeOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: TargetTypeFilter; label: string }[] {
  return [
    { value: 'all', label: t`All Types` },
    ...AUDIT_TARGET_TYPES.map((type) => ({
      value: type,
      label: getTargetTypeLabel(type, t),
    })),
  ];
}

// Group actions by category
function getActionOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: ActionFilter; label: string; category?: string }[] {
  const ACTION_CATEGORIES = getActionCategories(t);
  const options: { value: ActionFilter; label: string; category?: string }[] = [
    { value: 'all', label: t`All Actions` },
  ];

  // Group actions by prefix (category)
  const actionKeys = Object.keys(AUDIT_ACTIONS) as Array<keyof typeof AUDIT_ACTIONS>;
  const grouped = new Map<string, Array<keyof typeof AUDIT_ACTIONS>>();

  for (const action of actionKeys) {
    const prefix = action.split(':')[0];
    if (!grouped.has(prefix)) {
      grouped.set(prefix, []);
    }
    grouped.get(prefix)!.push(action);
  }

  for (const [category, actions] of grouped) {
    for (const action of actions) {
      options.push({
        value: action,
        label: AUDIT_ACTIONS[action],
        category: ACTION_CATEGORIES[category] || category,
      });
    }
  }

  return options;
}

export function AdminAuditLogs() {
  const { t, i18n } = useLingui();
  const [targetTypeFilter, setTargetTypeFilter] = useState<TargetTypeFilter>('all');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const limit = 50;
  const TARGET_TYPE_OPTIONS = getTargetTypeOptions(t);

  // Build query params
  const queryParams: AuditLogQueryParams = useMemo(() => {
    const params: AuditLogQueryParams = {
      page,
      limit,
    };

    if (targetTypeFilter !== 'all') {
      params.targetType = targetTypeFilter;
    }

    if (actionFilter !== 'all') {
      params.action = actionFilter;
    }

    if (search.trim()) {
      params.search = search.trim();
    }

    return params;
  }, [page, limit, targetTypeFilter, actionFilter, search]);

  const { data, isLoading, error } = useAuditLogs(queryParams);

  const handleTargetTypeChange = (filter: TargetTypeFilter) => {
    setTargetTypeFilter(filter);
    setPage(1);
  };

  const handleActionChange = (filter: ActionFilter) => {
    setActionFilter(filter);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const clearFilters = () => {
    setTargetTypeFilter('all');
    setActionFilter('all');
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters =
    targetTypeFilter !== 'all' || actionFilter !== 'all' || search.trim() !== '';

  const totalPages = data?.pagination.totalPages || 1;
  const total = data?.pagination.total || 0;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const actionOptions = useMemo(() => getActionOptions(t), [t]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <ClipboardDocumentListIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Audit Logs</Trans>
            </h1>
            <p className="text-muted-foreground">
              <Trans>Track admin actions and changes</Trans>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={cn('rounded-xl gap-2', hasActiveFilters && 'border-primary text-primary')}
        >
          <FunnelIcon className="h-4 w-4" />
          <Trans>Filters</Trans>
          {hasActiveFilters && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              <Trans>Active</Trans>
            </Badge>
          )}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="rounded-xl">
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t`Search in details...`}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 rounded-lg"
              />
            </div>

            {/* Target Type filter */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-muted-foreground">
                <Trans>Target Type:</Trans>
              </span>
              <div className="flex flex-wrap gap-2">
                {TARGET_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTargetTypeChange(option.value)}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      targetTypeFilter === option.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Action filter dropdown */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-muted-foreground">
                <Trans>Action:</Trans>
              </span>
              <select
                value={actionFilter}
                onChange={(e) => handleActionChange(e.target.value as ActionFilter)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {actionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.category ? `${option.category}: ${option.label}` : option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5">
                  <XMarkIcon className="h-4 w-4" />
                  <Trans>Clear filters</Trans>
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Audit logs table */}
      <Card className="rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive">
              <Trans>Failed to load audit logs: {error.message}</Trans>
            </div>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              <Trans>No audit logs found</Trans>
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-4 rounded-xl">
                <Trans>Clear filters</Trans>
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Timestamp</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Admin</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Action</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Target</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Details</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((log) => (
                    <AuditLogRow key={log.id} log={log} onSelect={setSelectedLog} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Showing {startItem}-{endItem} of {total.toLocaleString(i18n.locale)} logs
                </Trans>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Previous</Trans>
                  </span>
                </Button>
                <span className="text-sm text-muted-foreground">
                  <Trans>
                    Page {page} of {totalPages}
                  </Trans>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Next</Trans>
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail sheet */}
      {selectedLog && (
        <AuditLogDetailSheet log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}
